import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';

import { StudentService } from '../../services/student.service';
import { ActivityService } from '../../services/activity.service';

import { Student } from '../../interfaces/student.interfaces';
import { Activity } from '../../interfaces/activity.interfaces';
import { RouterLink } from '@angular/router';

interface AdminActivitySummary {
  activity: Activity;
  registeredStudents: number;
}

@Component({
  selector: 'app-admin-home',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './admin-home.html',
  styleUrl: './admin-home.scss',
})
export class AdminHome implements OnInit {
  private studentService = inject(StudentService);
  private activityService = inject(ActivityService);
  private cdr = inject(ChangeDetectorRef);

  students: Student[] = [];
  activities: Activity[] = [];

  totalStudents = 0;
  totalActivities = 0;
  totalRegistrations = 0;
  pendingValidations = 0;

  upcomingActivities: AdminActivitySummary[] = [];

  isLoading = true;

  ngOnInit(): void {
    this.loadDashboard();
  }

  private loadDashboard(): void {
    forkJoin({
      students: this.studentService.getAllStudents(),
      activities: this.activityService.getAllActivities(),
    }).subscribe({
      next: ({ students, activities }) => {
        this.students = students;
        this.activities = activities;

        this.calculateMetrics();
        this.buildUpcomingActivities();

        this.isLoading = false;
        this.cdr.detectChanges();

        console.log('ADMIN STUDENTS:', students);
        console.log('ADMIN ACTIVITIES:', activities);
        console.log('ADMIN METRICS:', {
          totalStudents: this.totalStudents,
          totalActivities: this.totalActivities,
          totalRegistrations: this.totalRegistrations,
          pendingValidations: this.pendingValidations,
        });
      },

      error: (error: unknown) => {
        console.error(
          'ERROR CARGANDO DASHBOARD ADMIN:',
          error
        );

        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private calculateMetrics(): void {
    this.totalStudents = this.students.length;
    this.totalActivities = this.activities.length;

    this.totalRegistrations = this.students.reduce(
      (total, student) =>
        total + (student.assignedActivities?.length ?? 0),
      0
    );

    this.pendingValidations = this.students.reduce(
      (total, student) => {
        const pending =
          student.assignedActivities?.filter(
            (activity) =>
              (activity.validatedHours ?? 0) === 0
          ).length ?? 0;

        return total + pending;
      },
      0
    );
  }

  private buildUpcomingActivities(): void {
    const now = new Date();

    this.upcomingActivities = this.activities
      .filter((activity) => {
        if (!activity.dueDate) {
          return true;
        }

        return new Date(activity.dueDate) >= now;
      })
      .sort(
        (a, b) =>
          new Date(a.dueDate).getTime() -
          new Date(b.dueDate).getTime()
      )
      .slice(0, 5)
      .map((activity) => ({
        activity,
        registeredStudents:
          this.countRegistrations(activity.id),
      }));
  }

  countRegistrations(activityId: string): number {
    return this.students.reduce(
      (total, student) => {
        const registered =
          student.assignedActivities?.some(
            (assigned) =>
              String(assigned.activityRef) ===
              String(activityId)
          );

        return total + (registered ? 1 : 0);
      },
      0
    );
  }
}